import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, Observable, filter } from 'rxjs';

export type UnifiedCartTab = 'native' | 'exclusives';

const AUTO_CLOSE_MS = 5000;

@Injectable({ providedIn: 'root' })
export class UnifiedCartUIService {
  private readonly openSubject = new BehaviorSubject<boolean>(false);
  private readonly activeTabSubject = new BehaviorSubject<UnifiedCartTab>('native');
  private autoCloseTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(private readonly router: Router) {
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => {
        if (this.openSubject.value) this.close();
      });
  }

  open$(): Observable<boolean> {
    return this.openSubject.asObservable();
  }

  activeTab$(): Observable<UnifiedCartTab> {
    return this.activeTabSubject.asObservable();
  }

  get isOpen(): boolean {
    return this.openSubject.value;
  }

  get activeTab(): UnifiedCartTab {
    return this.activeTabSubject.value;
  }

  open(tab?: UnifiedCartTab, autoClose = false): void {
    if (tab) this.activeTabSubject.next(tab);
    this.openSubject.next(true);
    this.cancelAutoClose();
    if (autoClose) {
      this.autoCloseTimer = setTimeout(() => {
        this.close();
        this.autoCloseTimer = null;
      }, AUTO_CLOSE_MS);
    }
  }

  close(): void {
    this.cancelAutoClose();
    this.openSubject.next(false);
  }

  setActiveTab(tab: UnifiedCartTab): void {
    this.activeTabSubject.next(tab);
    this.cancelAutoClose();
  }

  cancelAutoClose(): void {
    if (this.autoCloseTimer) {
      clearTimeout(this.autoCloseTimer);
      this.autoCloseTimer = null;
    }
  }
}
